'use client';

import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useAuth } from "./auth-context";
import { useFirestore } from "@/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { isAccessExpired } from "@/lib/subscriptions";
import { normalizeBundleGrants, type BundleGrant } from "@/lib/product-bundles";

interface OwnedProductsContextValue {
    /** Products the user can access RIGHT NOW. A lapsed subscription
     *  entitlement is excluded. See the accessUntil handling below. */
    ownedProductIds: Set<string>;
    isOwned: (productId: string) => boolean;
    /** Epoch millis at which a still-live entitlement expires, for the products
     *  that have a deadline. Absent for anything bought outright. Lets a caller
     *  say "access until X" without re-reading the subscription. */
    accessUntil: Map<string, number>;
    /**
     * The tier each owned tiered product was bought at, and the bundle that
     * granted a product when one did (src/lib/product-bundles.ts), for the
     * checkout and the library to say "you own this at another tier".
     */
    ownedTierId: Map<string, string>;
    viaBundleId: Map<string, string>;
    /** What each owned bundle grants this buyer, by bundle id, every later addition included. */
    bundleGrants: Map<string, BundleGrant[]>;
    isLoaded: boolean;
    /**
     * The entitlement listener failed (a permission or network error). It
     * still reports `isLoaded`, so spinners stop, but an empty set then means
     * "unknown", not "owns nothing": a surface that would say so shows a load
     * error instead.
     */
    error: boolean;
}

const OwnedProductsContext = createContext<OwnedProductsContextValue>({
    ownedProductIds: new Set(),
    isOwned: () => false,
    accessUntil: new Map(),
    ownedTierId: new Map(),
    viaBundleId: new Map(),
    bundleGrants: new Map(),
    isLoaded: false,
    error: false,
});

interface OwnedEntry {
    productId: string;
    /** Set only by cancelSubscriptionInternal; absent means never expires. */
    accessUntil?: number;
    tierId?: string;
    viaBundleId?: string;
    bundleItems?: BundleGrant[];
}

export function OwnedProductsProvider({ children }: { children: React.ReactNode }) {
    const { authUser, isUserLoading } = useAuth();
    const firestore = useFirestore();
    const [entries, setEntries] = useState<OwnedEntry[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(false);
    // Advanced by the timer below so an entitlement that lapses while the tab is
    // open drops out of the set without a reload.
    const [now, setNow] = useState(() => Date.now());

    const uid = authUser?.uid;
    useEffect(() => {
        if (!uid) {
            setEntries([]);
            // "Loaded" describes the entitlement listener, not the absence of a
            // user. Firebase restores the session from IndexedDB asynchronously,
            // so `authUser` is null for the first few hundred ms of every page
            // load. Reporting true there told every consumer "signed in, owns
            // nothing", which is how a real customer lost the Message button on
            // a seller's profile. Only a settled signed-out state is loaded.
            setIsLoaded(!isUserLoading);
            setError(false);
            return;
        }

        // A user just arrived (or switched). Nothing is known about their
        // entitlements until the snapshot lands, and the previous account's
        // entries must not answer for them in the meantime.
        setEntries([]);
        setIsLoaded(false);
        setError(false);

        const ref = collection(firestore, 'users', uid, 'ownedProducts');
        const unsubscribe = onSnapshot(
            ref,
            snap => {
                setEntries(snap.docs.map(d => {
                    const data = d.data() ?? {};
                    return {
                        productId: d.id,
                        accessUntil: Number(data.accessUntil) || undefined,
                        tierId: data.tierId === undefined || data.tierId === null || data.tierId === '' ? undefined : String(data.tierId),
                        viaBundleId: typeof data.viaBundleId === 'string' && data.viaBundleId ? data.viaBundleId : undefined,
                        bundleItems: Array.isArray(data.bundleItems) ? normalizeBundleGrants(data.bundleItems) : undefined,
                    };
                }));
                setNow(Date.now());
                setError(false);
                setIsLoaded(true);
            },
            (err) => {
                // Logged, because a swallowed failure here reads exactly like
                // an account that owns nothing.
                console.error('[owned-products] snapshot failed:', err);
                setError(true);
                setIsLoaded(true);
            },
        );
        return () => unsubscribe();
        // Keyed on the uid, not the FirebaseUser object: auth-context clones
        // that object on every auth event (token refresh, emailVerified
        // flipping), and re-running this effect for those would now tear the
        // entitlement set down and re-report "not loaded" each time.
    }, [uid, isUserLoading, firestore]);

    const { ownedProductIds, accessUntil, ownedTierId, viaBundleId, bundleGrants } = useMemo(() => {
        const ids = new Set<string>();
        const deadlines = new Map<string, number>();
        const tiers = new Map<string, string>();
        const bundles = new Map<string, string>();
        const grants = new Map<string, BundleGrant[]>();
        for (const entry of entries) {
            if (isAccessExpired(entry.accessUntil, now)) continue;
            ids.add(entry.productId);
            if (entry.accessUntil) deadlines.set(entry.productId, entry.accessUntil);
            if (entry.tierId) tiers.set(entry.productId, entry.tierId);
            if (entry.viaBundleId) bundles.set(entry.productId, entry.viaBundleId);
            if (entry.bundleItems && entry.bundleItems.length > 0) grants.set(entry.productId, entry.bundleItems);
        }
        // A bundle's products are owned from the moment its own entitlement
        // lists them: onOwnedProductWritten writes each one's record a moment
        // later, and /api/download accepts the bundle's list meanwhile.
        for (const list of grants.values()) {
            for (const grant of list) ids.add(grant.productId);
        }
        return { ownedProductIds: ids, accessUntil: deadlines, ownedTierId: tiers, viaBundleId: bundles, bundleGrants: grants };
    }, [entries, now]);

    // Re-evaluate exactly when the soonest deadline passes, rather than polling.
    // The download API re-checks server-side regardless, so this is only about
    // the UI not offering a Download button that would 403.
    useEffect(() => {
        let soonest = Infinity;
        for (const ms of accessUntil.values()) {
            if (ms > now && ms < soonest) soonest = ms;
        }
        if (soonest === Infinity) return;
        // setTimeout saturates past ~24.8 days and would fire immediately, so
        // cap the wait and let the next tick re-arm for anything further out.
        const delay = Math.min(soonest - now + 1000, 6 * 60 * 60 * 1000);
        const timer = setTimeout(() => setNow(Date.now()), delay);
        return () => clearTimeout(timer);
    }, [accessUntil, now]);

    return (
        <OwnedProductsContext.Provider value={{
            ownedProductIds,
            isOwned: (id: string) => ownedProductIds.has(id),
            accessUntil,
            ownedTierId,
            viaBundleId,
            bundleGrants,
            isLoaded,
            error,
        }}>
            {children}
        </OwnedProductsContext.Provider>
    );
}

export const useOwnedProducts = () => useContext(OwnedProductsContext);
